import jQuery from 'jquery';
import {findInContext, isDescendant} from "../util/jquery-ext";
import {deviceSupportsTouch} from "../util/touch-support";
import {removeFocusAfterClick} from "../util/focus";

const ITEM_CLASS = "toggle-item";
const BUTTON_CLASS = ITEM_CLASS + "__button";
const SELECT_BUTTON_CLASS = ITEM_CLASS + "__select";
const CONTENT_CLASS = ITEM_CLASS + "__content";
const ACTIVE_STATE = "--active";

export const defaultOptions = {
  activeClass: ITEM_CLASS + ACTIVE_STATE,
  button: '> .' + BUTTON_CLASS, // selector for the toggle button relative to the item or a jQuery object
  buttonActiveClass: BUTTON_CLASS + ACTIVE_STATE,
  selectButton: '', // selector for the select button relative to the item or a jQuery object
  selectButtonActiveClass: SELECT_BUTTON_CLASS + ACTIVE_STATE,
  hover: '', // selector for hover area relative to the item or a jQuery object
  touchHover: '', // selector for emulated hover area relative to the item or a jQuery object
  content: '> .' + CONTENT_CLASS, // selector for the toggleable content relative to the item or a jQuery object
  contentActiveClass: CONTENT_CLASS + ACTIVE_STATE,
  initial: false, // initial state is active if set to true or containing a selector which matches the item
  closeOnBlur: false, // if true, a click outside of the item's element deactivates the item
  aria: true, // Enables the ARIA support.
  ariaButtonAttribute: 'aria-expanded', // Name of the ARIA state attribute for the toggle button.
  ariaSelectButtonAttribute: 'aria-selected' // Name of the ARIA state attribute for the select button.
};

export const ToggleItem = {

  options: defaultOptions,

  _create: function () {
    this.element.addClass(ITEM_CLASS);
    this.content = this._getComponent(this.options.content);
    this.hoverArea = this._getComponent(this.options.hover);
    this.touchHoverArea = this._getComponent(this.options.touchHover);
    this.setButton(this.options.button);
    this.setSelectButton(this.options.selectButton);

    // Assuming touch devices do not support hover.
    if (!deviceSupportsTouch) {
      this.hoverArea
        .on('mouseenter', (e) => {
          this.activate();
        })
        .on('mouseleave', (e) => {
          this.deactivate();
        });
    }
    else {
      this.touchHoverArea
        .on('click', (e) => {
          // Hover emulation: If the toggle button is currently invisible, clicking anywhere inside an inactive item's
          // touchHoverArea will activate it.
          if (!this.isActive() && !this.isButtonVisible()) {
            e.preventDefault();
            this.activate();
          }
        });
    }

    if (this.options.closeOnBlur) {
      // Close toggle item if clicked outside
      // @see https://stackoverflow.com/questions/37573608/how-to-make-modal-close-on-click-outside
      jQuery('body').on('click', (e) => {
        if (this.isActive() && !isDescendant(jQuery(e.target), this.element)) {
          this.deactivate();
        }
      });
    }

    this._setState(this._getInitialState());
    this.initialized = true;
  },

  setButton(button) {
    button = this._getComponent(button);
    if (button.length) {
      button.attr('role', this.options.aria ? 'button' : 'none');
      button.on('click', (e) => {
        e.preventDefault();
        this.toggle(null, e);
      });
      removeFocusAfterClick(button);
    }
    this.button = button;
    if (this.initialized) {
      this._setState(this.isActive());
    }
  },

  setSelectButton(button) {
    button = this._getComponent(button);
    if (button.length) {
      button.attr('role', this.options.aria ? 'button' : 'none');
      button.on('click', (e) => {
        e.preventDefault();
        this.activate(e);
      });
      removeFocusAfterClick(button);
    }
    this.selectButton = button;
    if (this.initialized) {
      this._setState(this.isActive());
    }
  },

  _getComponent: function (selectorOrElement) {
    return selectorOrElement instanceof jQuery ? selectorOrElement : findInContext(this.element, selectorOrElement);
  },

  _getInitialState: function () {
    if (typeof(this.options.initial) === 'string') {
      return this.element.is(this.options.initial);
    }
    return this.options.initial;
  },

  isActive: function () {
    return this.element.hasClass(this.options.activeClass);
  },

  isButtonVisible: function () {
    return this.button.is(':visible') && this.button.height() > 1;
  },

  _setState: function (active) {
    this.element.toggleClass(this.options.activeClass, active);
    this.content.toggleClass(this.options.contentActiveClass, active);
    this.button.toggleClass(this.options.buttonActiveClass, active);
    this.selectButton.toggleClass(this.options.selectButtonActiveClass, active);
    if (this.options.aria) {
      this.button.attr(this.options.ariaButtonAttribute, active ? 'true' : 'false');
      this.selectButton.attr(this.options.ariaSelectButtonAttribute, active ? 'true' : 'false');
    }
  },

  toggle: function (active, event) {
    if (typeof active !== 'boolean') {
      active = !this.isActive();
    }

    if (active && this._trigger("beforeActivate", event) === false) {
      // activate canceled
      active = false;
    }
    this._setState(active);
    this._trigger("afterToggle", event, {active});
  },

  activate: function (event) {
    this.toggle(true, event);
  },

  deactivate: function (event) {
    this.toggle(false, event);
  }
};
